import { lazy } from 'react';

/**
 * Drop-in replacement for `React.lazy` that recovers from stale-chunk errors.
 *
 * When we deploy a new admin build, hashed asset filenames change. Any tab
 * that's been open across the deploy will fail to fetch the old chunk on the
 * next route navigation. Different browsers report this differently:
 *
 *   - Chrome / Firefox / Vite: "Failed to fetch dynamically imported module"
 *   - Vite alt:                "error loading dynamically imported module"
 *   - Safari / iOS Safari:     "Importing a module script failed."
 *   - Webpack-style chunks:    "Loading chunk \d+ failed"
 *   - Edge cases:              generic TypeError where filename URL is in the
 *                              error (we sniff the URL/file shape too)
 *
 * We catch all of these and force one hard reload so the page picks up the
 * new bundle. Guarded against reload loops: skips the reload if we already
 * did one within the last 10 seconds (in which case the failure is real,
 * not stale, and we let React's ErrorBoundary surface it).
 */
const RELOAD_KEY = 'lazyWithRetry:lastReload';
const RELOAD_COOLDOWN_MS = 10_000;

// Each pattern matches one browser/bundler's flavor of "old chunk gone."
// Listed permissively — false positives only cost a single reload and are
// rate-limited by RELOAD_COOLDOWN_MS.
const CHUNK_ERROR_PATTERNS = [
  /Failed to fetch dynamically imported module/i,   // Chrome, Firefox, Vite
  /error loading dynamically imported module/i,     // Vite alt
  /Importing a module script failed/i,              // Safari, iOS Safari
  /Loading chunk\s+\d+\s+failed/i,                  // Webpack-style
  /Loading CSS chunk\s+\d+\s+failed/i,              // Webpack CSS chunks
  /ChunkLoadError/i,                                // Webpack error name
  /Unable to preload CSS/i,                         // Vite CSS preload
];

function looksLikeStaleChunk(err) {
  if (!err) return false;
  const message = err?.message || '';
  const stringified = String(err);
  // Also check the request URL on the error (some browsers stash the failed
  // module URL on err.url / err.target.src — the dynamic-import filename will
  // include a hash that no longer exists on the server).
  const url = err?.url || err?.target?.src || '';
  const haystack = `${message}\n${stringified}\n${url}`;
  return CHUNK_ERROR_PATTERNS.some((re) => re.test(haystack));
}

export const lazyWithRetry = (importFn) => {
  return lazy(async () => {
    try {
      return await importFn();
    } catch (err) {
      if (!looksLikeStaleChunk(err)) throw err;

      const last = parseInt(sessionStorage.getItem(RELOAD_KEY) || '0', 10);
      const now = Date.now();
      if (now - last > RELOAD_COOLDOWN_MS) {
        sessionStorage.setItem(RELOAD_KEY, String(now));
        window.location.reload();
        // Return a placeholder so React.lazy resolves cleanly while the
        // reload happens. The component is never actually rendered.
        return { default: () => null };
      }
      throw err;
    }
  });
};

// Exported for unit testing.
export const _isStaleChunkError = looksLikeStaleChunk;
